'use strict';

import * as THREE from 'three';
import { Vector3 } from 'three';

import { isDefined } from '../../utils/variables';
import cameraManager from '../camera-manager';
import { FpsCameraController } from '../cameras/controllers/fps-control';
import { PanoramaSphere } from '../elements/panorama/panorama-sphere';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  KeyInputData,
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { Tool } from './tool';

type GetActivePanoramaSphere = () => PanoramaSphere | undefined;

export class Orient360PoiTowardsNorthTool extends Tool {
  getActivePanoramaSphere?: GetActivePanoramaSphere;

  constructor(inputHandler: InputHandler) {
    super('rotate-360-poi-north', inputHandler);
    this.visible = false;
  }

  /** World-space ray direction from screen (x,y). */
  private getRayDirectionWorld(x: number, y: number): THREE.Vector3 | undefined {
    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;

    const rayCaster = camera.getRay(x, y);
    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      pixelSnapThreshold,
      minPickingDistance,
    );
    return rayCaster.ray.direction.clone().normalize();
  }

  /**
   * Compute Δyaw (around parent-local +Y) needed so that the picked ray "faces north" (world +Y),
   * but as an **incremental** delta (for repeated clicks to keep rotating).
   *
   * We compute in PARENT-LOCAL:
   * - dirParent = (ray direction in parent-local)
   * - northParent = (+Y world in parent-local)
   * Δyaw = azimuth(northParent) - azimuth(dirParent)  (angles on parent-local XZ plane)
   */
  private computeDeltaYawForPick(x: number, y: number, sphere: PanoramaSphere): number | undefined {
    const dirWorld = this.getRayDirectionWorld(x, y);
    if (!dirWorld) {
      return;
    }

    const parent = sphere.parent;
    if (!parent) {
      return;
    }

    // World -> parent-local
    let qParentWorld = new THREE.Quaternion();
    qParentWorld = parent.getWorldQuaternion(qParentWorld);
    const qWorldToParent = qParentWorld.clone().invert();

    const dirParent = dirWorld.clone().applyQuaternion(qWorldToParent).normalize();
    const northParent = new THREE.Vector3(0, 1, 0).applyQuaternion(qWorldToParent).normalize();

    // Project onto parent-local XZ (yaw around parent-local +Y)
    const dXZ = new THREE.Vector2(dirParent.x, dirParent.z);
    const nXZ = new THREE.Vector2(northParent.x, northParent.z);

    // If projection is degenerate (near the Y axis), skip
    if (dXZ.lengthSq() < 1e-8 || nXZ.lengthSq() < 1e-8) {
      return;
    }

    const aDir = Math.atan2(dXZ.x, dXZ.y); // angle from +Z toward +X
    const aNorth = Math.atan2(nXZ.x, nXZ.y); // typically ~0, but computed for robustness

    // Δyaw so that rotating sphere by Δ will align the clicked azimuth with "north"
    let delta = aNorth - aDir;

    // Normalize to [-PI, PI] to avoid big jumps
    const twoPi = Math.PI * 2;
    delta = ((((delta + Math.PI) % twoPi) + twoPi) % twoPi) - Math.PI;

    return delta;
  }

  /** Apply an incremental local-Y yaw to the sphere (quaternion multiply; no Euler). */
  private applyDeltaYaw(sphere: PanoramaSphere, deltaYaw: number, syncPoi = true) {
    if (Math.abs(deltaYaw) < 1e-8) {
      return;
    }

    const qDelta = new THREE.Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), deltaYaw);
    // Local-space rotation: post-multiply
    sphere.quaternion.multiply(qDelta);
    sphere.updateMatrixWorld();

    if (syncPoi) {
      const poi = sphere.getActivePoi();
      if (poi) {
        // Apply same local-Y delta to the POI for visual consistency.
        poi.quaternion.multiply(qDelta);
        poi.updateMatrixWorld?.();
      }
    }
  }

  private async onClickSnapToNorth(x: number, y: number) {
    const sphere = this.getActivePanoramaSphere?.();
    const poi = sphere?.getActivePoi();
    if (!isDefined(sphere) || !isDefined(poi) || !poi.is360Poi() || poi.has360RotationMetadata) {
      return;
    }

    const deltaYaw = this.computeDeltaYawForPick(x, y, sphere);
    if (deltaYaw === undefined) {
      return;
    }

    this.applyDeltaYaw(sphere, deltaYaw, true);

    // Zero camera yaw, to focus on the new "north" point.
    const cameraController = cameraManager().getActiveController();
    if (cameraController instanceof FpsCameraController) {
      // Reset camera yaw to 0 while keeping pitch intact
      const currentYaw = cameraController.yaw;

      // The applyRotation method applies sensitivity and zoom compensation
      // We need to calculate the delta that will result in yaw = 0
      // Since: newYaw = currentYaw + delta * sensitivity * zoomCompensation
      // We want: 0 = currentYaw + delta * sensitivity * zoomCompensation
      // So: delta = -currentYaw / (sensitivity * zoomCompensation)
      const activeCamera = cameraManager().getActiveCamera();
      const zoomCompensation = (activeCamera as any)?.getZoomAmountPercent?.() || 1.0;
      const effectiveSensitivity =
        cameraController.sensitivity * Math.max(0.3, 1.0 - zoomCompensation);
      const deltaX = -currentYaw / effectiveSensitivity;

      cameraController.applyRotation(deltaX, 0);

      publish(SubscriptionTopic.OnCameraMoved, { camera: cameraManager().getActiveCamera()! });
    }

    await poi.save();
  }

  enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
  }

  async disable() {
    this.visible = false;
    this.inputHandler.setCursor('');

    return Promise.resolve();
  }

  // --- Input -----------------------------------------------------------------

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;
    if (e.button.type === MouseButtonType.LeftButton) {
      void this.onClickSnapToNorth(e.pointer.position.x, e.pointer.position.y);
    }
    return true;
  }

  onMouseMove(_: MouseInputData) {
    return true;
  }

  async onMouseUp(e: MouseInputData) {
    if (e.button.type === MouseButtonType.RightButton) {
      if (this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
        this.inputHandler.setTool('select');
      }
    }

    return Promise.resolve(true);
  }

  // Touch stubs
  onTouchStart(e: TouchInputData) {
    if (e.touches.length < 1) {
      return true;
    }
    return true;
  }
  onTouchMove(e: TouchInputData) {
    if (e.touches.length < 1) {
      return true;
    }
    return true;
  }
  onTouchEnd(_: TouchInputData) {
    return true;
  }

  onKeyDown(_input: KeyInputData) {
    /* no-op */
  }
}
